/**
 * ⟦DEV · A-176⟧ **La télécommande, alias le mode triche** — se poser où on veut dans la partie, en un clic.
 *
 * **Pourquoi ça existe.** Atteindre 1 000 abonnés demande des dizaines de vidéos jouées à la
 * main : toute la seconde moitié du jeu — le Grand Saut ⟦A-1⟧, la bibliothèque, les evergreen,
 * la faillite, le million — était **intestable** en pratique. On ne vérifie pas ce qu'on ne
 * peut pas atteindre.
 *
 * ## Le mode triche ⟦A-176 · C59⟧ : trois verrous, et ils ne sont pas négociables
 *
 * La télécommande n'est plus réservée au développement : elle part dans le build de production
 * sous le nom de **mode triche**, invisible tant qu'on ne l'a pas allumée. Le verrou
 * `import.meta.env.DEV` d'avant (Vite emportait tout le module au build) est donc levé, **à la
 * demande de Matt** (03/10/2026), et remplacé par deux verrous qui, eux, tiennent en production.
 *
 * 1. **Un opt-in explicite, jamais un défaut.** Le panneau ne s'affiche que s'il a été armé, par
 *    l'une de ces trois voies, mémorisée en `sessionStorage` (React Router perd les paramètres
 *    en naviguant, le piège est connu ; fermer l'onglet éteint) :
 *    - **l'URL dédiée** `?triche` (alias `?dev=1`), éteinte par `?triche=off`. C'est la voie
 *      « pour l'instant » de Matt. **`TRICHE_PAR_URL` la coupe, seule, au lancement public**
 *      sans toucher à rien d'autre ;
 *    - **le cheat code** : le code Konami (↑ ↑ ↓ ↓ ← → ← → B A) tapé au clavier n'importe où ;
 *    - **la voie tactile** : sept tapes rapides sur la télé MeTube (le logo violet, à l'écran-titre,
 *      à la création et en haut de la chambre).
 *    L'armement par code ou par tapes se signale par un toast ; il n'existe qu'en bac à sable.
 * 2. **Un seul terrain : le bac à sable.** Ce module écrit dans le store local, point. Il ne
 *    cherche pas à tricher contre l'API : le serveur ne fait jamais confiance au client
 *    (SPEC-API §6.4), et il n'y a donc aucune porte de ce côté. En mode compte, le panneau ne
 *    s'affiche pas, le code ne l'arme pas, et `remplacerRun` refuse.
 * 3. **Un garde-fou d'exécution.** `localGameClient.remplacerRun`, l'unique chemin d'écriture
 *    d'un état fabriqué, refuse tant que le mode triche n'est pas armé : la promesse tient au
 *    runtime, pas seulement parce que le panneau est caché.
 *
 * `?devScore` (l'Atelier) reste, lui, strictement `import.meta.env.DEV` : hors de ce chantier.
 *
 * ## Ce qui fait la différence entre un outil et un générateur d'états cassés
 *
 * Poser `subscribers = 1000` **ne suffit pas** à faire vivre le Grand Saut : le franchissement
 * est un événement de progression qui verse `CAPITAL_DEPART` et allume les charges via
 * `flags.grand_saut_done`. Un bouton « aller à 1 000 » qui oublie le drapeau donne une boîte à
 * Clicks vide et un joueur qui ne comprend rien.
 *
 * D'où la règle de ce fichier : **on ne pose jamais un champ à la main quand le moteur sait le
 * poser.** Toute retouche passe par `recoller()`, qui rejoue les pas 9 et 10 de la file de
 * résolution (§2.3) — clamps, image, `appliquerProgression` (niveaux, paliers, PC, Grand Saut),
 * descente vers la faillite. Aucune règle de jeu n'est réécrite ici, aucun chiffre n'est
 * recopié : tout vient de `@maxime/shared`.
 *
 * **Une seule chose n'est pas posée, volontairement : la victoire.** Elle est gravée par
 * `progressionEtFins` (`shared/src/engine/index.ts`), qui n'est pas exporté. La recopier ici
 * serait exactement le genre de duplication que CLAUDE.md interdit — donc après s'être posé au
 * million, la victoire tombe **à l'action suivante**, posée par le moteur lui-même.
 */

import {
  FANTOME_FLOPS,
  FANTOME_MOMENTUM_PLANCHER,
  FANTOME_RENTE_NULLE_H,
  H_FAILLITE,
  H_JOUR,
  PALIERS,
  PALIER_GRAND_SAUT,
  PUBLICATIONS_ESCALIER,
  accorderSurcharge,
  actionCost,
  appliquerClamps,
  appliquerProgression,
  chargesDuJour,
  frontieresDeJour,
  majFaillite,
  neutraliserImage,
  nuitHook,
  recalculerImage,
  verserFrontiereDeJour,
  type EngineEvent,
  type GameState,
  type LogEntry,
} from "@maxime/shared";

import { contenu } from "./content";
import { etagesOuverts } from "./escalier";
import type { JournalEntry, RunSnapshot } from "./gameClient";
import { entreeInterface, entreesDeResolution, limiterJournal } from "./journal";
import { interrupteurs } from "./menage";

// ─────────────────────────────────────────────────────────────────────────────
// L'interrupteur
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⟦A-176⟧ **L'URL dédiée allume-t-elle le mode triche ?** `true` tant que Matt s'en sert pour
 * tester ; **à passer à `false` au lancement public**, et c'est tout : `?triche`, `?triche=off` et
 * l'alias `?dev=1` sont alors ignorés, seuls le code Konami et les sept tapes sur la télé MeTube
 * restent. Rien d'autre à toucher (le panneau, le garde-fou et les tests lisent cette constante).
 */
export const TRICHE_PAR_URL = true;

/** Même forme de clé que `maxime:devScore` : un seul vocabulaire pour les deux raccourcis. */
const CLE_TRICHE = "maxime:triche";

/** Par quelle voie le mode triche s'arme. */
export type VoieTriche = "url" | "code" | "tapes";

/**
 * Le code Konami, en valeurs de `KeyboardEvent.key` passées en minuscules (la lettre tapée, pas la
 * touche physique : un clavier AZERTY donne les mêmes `b` et `a`).
 */
export const CODE_KONAMI: readonly string[] = [
  "arrowup",
  "arrowup",
  "arrowdown",
  "arrowdown",
  "arrowleft",
  "arrowright",
  "arrowleft",
  "arrowright",
  "b",
  "a",
];

/**
 * Pousse une touche dans une fenêtre glissante de la longueur du code. **Une fenêtre glissante, pas
 * un « on repart de zéro à la première erreur »** : avec ce dernier, `↑ ↑ ↑ ↓ ↓…` (un ↑ de trop)
 * échouerait alors que les dix dernières touches ne sont qu'à un cran du code.
 */
export function avancerCode(
  tampon: readonly string[],
  touche: string,
): { tampon: string[]; complet: boolean } {
  const suite = [...tampon, touche.toLowerCase()].slice(-CODE_KONAMI.length);
  const complet = suite.length === CODE_KONAMI.length && suite.every((t, i) => t === CODE_KONAMI[i]);
  return { tampon: complet ? [] : suite, complet };
}

/** Sept tapes sur la télé MeTube, chacune moins de 700 ms après la précédente. */
export const TAPES_REQUISES = 7;
export const TAPES_ECART_MS = 700;

/** Compte une tape de plus (`maintenant` en ms) ; trop tardive, elle repart de un. */
export function compterTape(
  precedente: { n: number; t: number } | null,
  maintenant: number,
): { n: number; t: number; complet: boolean } {
  const n = precedente && maintenant - precedente.t <= TAPES_ECART_MS ? precedente.n + 1 : 1;
  return n >= TAPES_REQUISES ? { n: 0, t: maintenant, complet: true } : { n, t: maintenant, complet: false };
}

let armee: boolean | null = null; // `null` : pas encore lue
const ecouteurs = new Set<() => void>();

/**
 * Lecture initiale, une seule fois : l'URL d'abord (si `TRICHE_PAR_URL`), puis la mémoire de
 * l'onglet. `?triche` seul (valeur vide) allume ; `off`, `0` et `false` éteignent.
 */
export function lireArmement(urlPermise: boolean = TRICHE_PAR_URL): boolean {
  try {
    if (urlPermise) {
      const parametres = new URLSearchParams(window.location.search);
      const brut = parametres.get("triche") ?? parametres.get("dev");
      if (brut !== null) {
        if (brut === "off" || brut === "0" || brut === "false") {
          window.sessionStorage.removeItem(CLE_TRICHE);
          return false;
        }
        window.sessionStorage.setItem(CLE_TRICHE, "1");
        return true;
      }
    }
    return window.sessionStorage.getItem(CLE_TRICHE) === "1";
  } catch {
    // Navigation privée, stockage refusé, pas de `window` : pas de panneau, le jeu reste jouable.
    return false;
  }
}

/**
 * Le mode triche est-il armé ? C'est l'instantané de `useSyncExternalStore` (stable : un booléen).
 * N'a aucun effet en mode compte : voir le verrou 2 dans `PanneauDev` et `remplacerRun`.
 */
export function modeTricheActif(): boolean {
  if (armee === null) armee = lireArmement();
  return armee;
}

/** Abonnement au changement d'armement (pour `useSyncExternalStore`). */
export function ecouterTriche(rappel: () => void): () => void {
  ecouteurs.add(rappel);
  return () => ecouteurs.delete(rappel);
}

function poserArmement(valeur: boolean): void {
  armee = valeur;
  try {
    if (valeur) window.sessionStorage.setItem(CLE_TRICHE, "1");
    else window.sessionStorage.removeItem(CLE_TRICHE);
  } catch {
    // Stockage refusé : le mode vit en mémoire pour cet onglet, il ne survivra pas au F5.
  }
  for (const rappel of ecouteurs) rappel();
}

/** Arme le mode triche (code Konami, tapes). Rend `false` s'il l'était déjà. */
export function armerTriche(): boolean {
  if (modeTricheActif()) return false;
  poserArmement(true);
  return true;
}

/** Éteint le mode triche pour cet onglet. */
export function eteindreTriche(): void {
  if (modeTricheActif()) poserArmement(false);
}

/** Pour les tests : oublie la lecture initiale, la prochaine relira l'URL et la mémoire. */
export function reinitialiserTriche(): void {
  armee = null;
  ecouteurs.clear();
}

// ─────────────────────────────────────────────────────────────────────────────
// Le cœur : retoucher un état, puis le recoller
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Les pas 9 et 10 de la file de résolution (§2.3), rejoués sur un état retouché à la main.
 * **MUTE `state`** — comme les modules du moteur, et sur un clone.
 *
 * L'ordre est celui de la spec, pas un ordre de confort : clamps, puis image, puis progression
 * (c'est elle qui crédite les paliers, les PC et déclenche le Grand Saut ⟦A-1⟧), puis la
 * descente vers la faillite, puis clamps à nouveau parce que la progression a pu bouger des
 * jauges.
 *
 * ⟦C60 · A-177⟧ **Avec les interrupteurs du ménage**, lus au même endroit que le moteur (`interrupteurs()`) :
 * sans eux, la télécommande rejouait le monde d'avant (le capital de 600 ₵ au Grand Saut, la descente vers
 * la faillite, l'image de chaîne) sur une partie qui n'en a plus.
 */
function recoller(state: GameState, events: EngineEvent[]): void {
  const sys = interrupteurs();
  appliquerClamps(state);
  // Sans alignement l'image de chaîne n'existe pas : le moteur la remet à zéro au pas 10, on fait de même.
  if (sys.alignement) recalculerImage(state);
  else neutraliserImage(state);
  appliquerProgression(state, events, sys);
  // `clockAvant = clock_h` : aucun temps ne s'est écoulé pendant une retouche, donc aucune
  // alerte de faillite ne se rejoue en boucle. La fonction remet aussi
  // `money_negative_since_h` à `null` quand on repasse dans le vert ou avant le Grand Saut
  // (et toujours, quand la faillite est coupée).
  majFaillite(state, state.clock_h, events, sys);
  appliquerClamps(state);
}

/**
 * Applique une retouche et rend la run neuve, journal compris.
 *
 * `unlocksSeen` est **rempli avec tout ce qui est ouvert** : on vient de se téléporter, on ne
 * veut pas les sept annonces de l'escalier ⟦A-5⟧ empilées à la prochaine action. Le bouton
 * « rejouer les annonces » (`rearmerAnnonces`) fait le chemin inverse quand c'est justement ce
 * qu'on veut tester.
 */
function retoucher(
  run: RunSnapshot,
  resume: string,
  muter: (state: GameState, events: EngineEvent[], log: LogEntry[]) => void,
): RunSnapshot {
  const state = structuredClone(run.state);
  const events: EngineEvent[] = [];
  const log: LogEntry[] = [];

  muter(state, events, log);
  recoller(state, events);

  let id = run.journal.reduce((max, e) => Math.max(max, e.id), 0) + 1;
  const nouvelles: JournalEntry[] = [
    entreeInterface(state, id++, { icone: "🛠", ton: "info", texte: `Mode triche : ${resume}` }),
    ...entreesDeResolution(events, log, state, id),
  ];

  const journal = [...run.journal, ...nouvelles];
  return {
    ...run,
    state,
    journal: limiterJournal(journal),
    unlocksSeen: etagesOuverts(state),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Les gestes du panneau
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Publications posées par une téléportation : **le dernier étage de l'escalier**, pas un
 * chiffre choisi ici. C'est ce seuil qui ouvre la longue et la bibliothèque, et c'est aussi
 * celui où la pratique a tout donné au plafond de compétence (`PLAFOND_PRATIQUE_VIDEOS`).
 * Se poser à un palier avec moins laisserait le jeu à moitié verrouillé — le piège exact que
 * cet outil doit éviter.
 */
const PUBLICATIONS_TELEPORT = PUBLICATIONS_ESCALIER.longue;

/** Les six paliers de la course (PLAN §4.13) — la liste du moteur, jamais une copie. */
export const PALIERS_DEV = PALIERS;

/**
 * Se poser à un palier avec un état **cohérent** : les abonnés, les publications qu'il faut
 * pour que l'escalier soit ouvert, et tout ce que la progression en déduit (paliers franchis,
 * PC, Grand Saut et son capital).
 */
export function poserPalier(run: RunSnapshot, cible: number): RunSnapshot {
  return retoucher(run, `on se pose à ${cible} abonnés`, (state) => {
    poserAbonnes(state, cible);
    state.counters.videos_published = Math.max(
      state.counters.videos_published,
      PUBLICATIONS_TELEPORT,
    );
  });
}

/** Régler les abonnés à une valeur libre, sans rien ouvrir d'autre. */
export function reglerAbonnes(run: RunSnapshot, cible: number): RunSnapshot {
  return retoucher(run, `abonnés → ${Math.max(0, Math.round(cible))}`, (state) => {
    poserAbonnes(state, cible);
  });
}

/**
 * Le pool volatil est **borné par le total** (§4.7) et `subs_at_last_sleep` sert de repère
 * d'anti-frustration (§10.4) : les laisser derrière ferait lire au moteur une croissance
 * fantôme de plusieurs milliers d'abonnés à la nuit suivante.
 */
function poserAbonnes(state: GameState, cible: number): void {
  const valeur = Math.max(0, Math.round(cible));
  state.subscribers = valeur;
  state.subs_volatile = Math.min(state.subs_volatile, valeur);
  state.counters.subs_at_last_sleep = valeur;
}

/** Ce que le panneau sait régler à la main. Tout est optionnel : on ne touche que ce qu'on donne. */
export interface Jauges {
  energy?: number;
  /**
   * ⟦A-20⟧ Points de **surcharge** à ouvrir au-dessus du plafond ordinaire.
   *
   * Sans ça, la télécommande ne pouvait tout simplement pas montrer la surcharge : `recoller()`
   * écrête l'énergie à `plafondEnergie(state)`, qui vaut 100 tant que rien ne l'a ouvert. Or la
   * seule façon de l'ouvrir en jouant est de dormir **deux nuits d'affilée** — et sur une run
   * avancée, un événement s'intercale presque toujours entre les deux et remet le compteur de
   * nuits consécutives à zéro. La mécanique était donc invisible **et** invérifiable.
   *
   * On n'écrit pas `energy_surcharge` à la main : `accorderSurcharge` borne à `SURCHARGE_MAX`,
   * et c'est elle qui décide, comme partout ailleurs.
   */
  surcharge?: number;
  mental?: number;
  reputation?: number;
  loyalty?: number;
  momentum?: number;
  money?: number;
  pc?: number;
}

/**
 * Remplir ou vider les jauges. Les clamps du moteur font foi : rien ne sort de ses bornes.
 *
 * ⚠️ L'ordre compte : la surcharge s'accorde **avant** que l'énergie soit posée, parce que c'est
 * elle qui ouvre le plafond que `recoller()` appliquera ensuite. L'inverse écrêterait à 100 puis
 * ouvrirait un plafond sur une jauge déjà rabotée — la jauge afficherait 100 pour un plafond de
 * 130, soit précisément le bug qu'on cherche à voir.
 */
export function reglerJauges(run: RunSnapshot, jauges: Jauges): RunSnapshot {
  const libelles = Object.entries(jauges).map(([cle, valeur]) => `${cle} ${valeur}`);
  return retoucher(run, `jauges : ${libelles.join(", ")}`, (state) => {
    if (jauges.surcharge !== undefined) {
      // Une valeur plus BASSE que la surcharge courante doit pouvoir la faire redescendre :
      // `accorderSurcharge` n'ajoute que du positif, on repart donc de zéro.
      state.energy_surcharge = 0;
      accorderSurcharge(state, jauges.surcharge);
    }
    if (jauges.energy !== undefined) state.energy = jauges.energy;
    if (jauges.mental !== undefined) state.mental = jauges.mental;
    if (jauges.reputation !== undefined) state.reputation = jauges.reputation;
    if (jauges.loyalty !== undefined) state.loyalty = jauges.loyalty;
    if (jauges.momentum !== undefined) state.momentum = jauges.momentum;
    if (jauges.pc !== undefined) state.pc = Math.max(0, Math.round(jauges.pc));
    // ⟦A-1⟧ L'argent n'existe pas avant le Grand Saut : le poser quand même donnerait une
    // jauge grisée avec un solde dedans — un état que le jeu ne peut pas produire.
    if (jauges.money !== undefined && state.flags.grand_saut_done) {
      state.money = Math.round(jauges.money);
    }
  });
}

/**
 * Mettre le compte à rebours de la faillite à `jours` de la fin (§11.5).
 *
 * Le délai vient de `H_FAILLITE`, pas d'un chiffre écrit ici : c'est la même horloge que celle
 * que `verifierGameOver` regarde.
 *
 * ⟦C60 · A-177⟧ **Sans objet quand la faillite est coupée** : le panneau ne propose plus le bouton (il ne
 * ferait rien, `majFaillite` efface la descente). La fonction reste, pour le jour où l'interrupteur se
 * rallume : rien n'est supprimé.
 */
export function faillitteImminente(run: RunSnapshot, jours: number): RunSnapshot {
  return retoucher(run, `faillite dans ${jours} jour(s)`, (state) => {
    state.money = -Math.abs(state.money) - 1;
    state.counters.money_negative_since_h = Math.max(
      0,
      state.clock_h - (H_FAILLITE - jours * H_JOUR),
    );
  });
}

/**
 * ⟦A-58⟧ **Armer « Le Fantôme »** (§9.4bis, voie « l'extinction »).
 *
 * Les deux gardes instantanées de la voie sont posées ici — 5 vidéos nulles d'affilée et le
 * momentum au plancher. **La troisième condition, elle, ne se pose pas : elle se laisse
 * couler.** L'horloge de l'extinction est la rente hebdomadaire, et elle se lit dans les
 * vidéos (`heuresDeRenteNulle`) : une chaîne sans evergreen l'a déjà à `clock_h`. Donc, en
 * pratique — **« +90 jours », puis ce bouton, puis une action** : la fin tombe au pas suivant.
 *
 * On ne back-date rien de force : poser une horloge à la main ici, c'est exactement le genre
 * de raccourci qui fabrique un état que le moteur ne sait pas produire (l'avertissement de
 * l'en-tête de ce fichier).
 */
export function extinctionImminente(run: RunSnapshot): RunSnapshot {
  return retoucher(run, "extinction armée", (state) => {
    state.counters.consecutive_flops = FANTOME_FLOPS;
    state.momentum = FANTOME_MOMENTUM_PLANCHER;
  });
}

/** Combien de jours simulés séparent encore la chaîne de l'extinction ? (Affichage du panneau.) */
export function joursAvantExtinction(state: GameState): number {
  let depuis = 0;
  for (const v of state.videos) if (v.evergreen && v.evergreen.anchor_h > depuis) {
    depuis = v.evergreen.anchor_h;
  }
  const nulleDepuis = Math.max(0, state.clock_h - depuis);
  return Math.max(0, Math.ceil((FANTOME_RENTE_NULLE_H - nulleDepuis) / H_JOUR));
}

/**
 * L'escalier ⟦A-5⟧ d'un coup.
 *
 * **Il monte aussi à 1 000 abonnés si on est en dessous, et ce n'est pas un effet de bord** :
 * le septième étage — l'argent — EST le Grand Saut ⟦A-1⟧, attaché au palier 1 000 (⟦A-11 §4⟧
 * : « l'étage argent, c'est à 1 000 abonnés, c'est sûr »). Ouvrir la boîte à Clicks sans le
 * palier la ferait apparaître vide, sans capital ni charges.
 */
export function toutDeverrouiller(run: RunSnapshot): RunSnapshot {
  return retoucher(run, "tout déverrouillé", (state) => {
    state.counters.videos_published = Math.max(
      state.counters.videos_published,
      PUBLICATIONS_TELEPORT,
    );
    if (state.subscribers < PALIER_GRAND_SAUT) poserAbonnes(state, PALIER_GRAND_SAUT);
  });
}

/** Réarmer les annonces de l'escalier : elles retomberont toutes à la prochaine action. */
export function rearmerAnnonces(run: RunSnapshot): RunSnapshot {
  return { ...run, unlocksSeen: [] };
}

/**
 * Avancer de `jours` journées simulées, **sans jouer**.
 *
 * On rejoue le pas 5 de la file (§2.3) — une frontière de jour verse les tranches de courbe,
 * débite les charges et érode la réputation en image chaos — puis la **nuit** (§2.4 par
 * `nuitHook` : rente evergreen, désabonnements, decay de momentum, tendances, strikes). Rien
 * n'est recalculé ici : chaque ligne appelle la fonction du moteur qui porte la règle, dans
 * l'ordre où `applyAction` les appelle.
 *
 * Un jour = un `turn`, comme une action : le journal reste lisible et le mode découverte
 * conserve sa règle « une leçon par tour ».
 */
export function avancerJours(run: RunSnapshot, jours: number): RunSnapshot {
  const n = Math.max(1, Math.round(jours));
  const c = contenu();
  const sys = interrupteurs();
  return retoucher(run, `+${n} jour(s) simulé(s)`, (state, events, log) => {
    for (let i = 0; i < n; i++) {
      state.turn += 1;

      // Pas 3 : la nuit rend son énergie et sa SM par la table des coûts (§2.2) — la lire
      // plutôt que d'écrire « +80 » ici, c'est ce qui empêche la valeur de dériver.
      const cout = actionCost({ type: "sleep" }, state);
      state.energy += cout.energy_delta;
      state.mental += cout.mental_delta;

      // Pas 4-5 : l'horloge avance d'une journée pleine, et chaque frontière verse.
      const avant = state.clock_h;
      state.clock_h += H_JOUR;
      for (const _frontiere of frontieresDeJour(avant, state.clock_h)) {
        verserFrontiereDeJour(state, events);
        // ⟦C60 · A-177⟧ Sans loyer ni charges, rien à débiter ; sans alignement, pas d'érosion de l'image.
        state.money -= chargesDuJour(state, sys);
        if (sys.alignement && state.channel_image <= -40) state.reputation -= 1;
      }

      // §2.4 : la nuit elle-même.
      const deltaH = state.clock_h - state.counters.last_sleep_h;
      state.nights += 1;
      nuitHook({ state, content: c, seed: run.seed, events, log }, deltaH);
      state.counters.subs_at_last_sleep = state.subscribers;
      state.counters.last_sleep_h = state.clock_h;
      appliquerClamps(state);
    }
  });
}
